import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Toaster } from "@/components/ui/sonner";
import { RoleDashboard } from "@/components/RoleDashboard";
import { ROLES_STAFF, ROLES_ADMIN_ONLY, ROLES_TIME_TRACKING, ROLES_EXPENSES, ROLES_ROSTER } from "@/lib/roles";
import usePageTracking from "@/lib/analytics/usePageTracking";

import Login from "@/pages/Login";
import RegisterParent from "@/pages/RegisterParent";
import DemoLanding from "@/pages/DemoLanding";
import Students from "@/pages/Students";
import StudentDetail from "@/pages/StudentDetail";
import Instructors from "@/pages/Instructors";
import InstructorDetail from "@/pages/InstructorDetail";
import Parents from "@/pages/Parents";
import StaffManagement from "@/pages/StaffManagement";
import Schedule from "@/pages/Schedule";
import Classrooms from "@/pages/Classrooms";
import ClassroomDetail from "@/pages/ClassroomDetail";
import Vehicles from "@/pages/Vehicles";
import VehicleDetail from "@/pages/VehicleDetail";
import VehicleIssues from "@/pages/VehicleIssues";
import Forms from "@/pages/Forms";
import FormSubmissionFill from "@/pages/FormSubmissionFill";
import Payments from "@/pages/Payments";
import Notifications from "@/pages/Notifications";
import Reports from "@/pages/Reports";
import TimeTracking from "@/pages/TimeTracking";
import Expenses from "@/pages/Expenses";
import Settings from "@/pages/Settings";
import CommunicationCenter from "@/pages/CommunicationCenter";
import ClassroomRosterPage from "@/pages/ClassroomRosterPage";
import ClassroomRosterPrintPage from "@/pages/ClassroomRosterPrintPage";
import NotFound from "@/pages/NotFound";

// Dashboard-layout child routes that are open to any authenticated user
// (role-gating already happens one level up, via the DashboardLayout's own
// ProtectedRoute). Kept as plain data so the JSX below stays a simple map
// instead of a long, repetitive list of near-identical <Route> lines.
const OPEN_DASHBOARD_ROUTES = [
  { path: "/", index: true, Component: RoleDashboard },
  { path: "/schedule", Component: Schedule },
  { path: "/students", Component: Students },
  { path: "/students/:studentId", Component: StudentDetail },
  { path: "/instructors/:instructorId", Component: InstructorDetail },
  { path: "/classrooms", Component: Classrooms },
  { path: "/classrooms/:classroomId", Component: ClassroomDetail },
  { path: "/vehicle-issues", Component: VehicleIssues },
  { path: "/forms", Component: Forms },
  { path: "/forms/:submissionId/fill", Component: FormSubmissionFill },
  { path: "/payments", Component: Payments },
  { path: "/notifications", Component: Notifications },
];

// Dashboard-layout child routes restricted to specific roles — each needs
// its own nested ProtectedRoute. Extracted into data for the same reason
// as above: this is what was making App.js read as one long repeated
// pattern rather than a clear routing table.
const ROLE_GATED_DASHBOARD_ROUTES = [
  { path: "/instructors", roles: ROLES_STAFF, Component: Instructors },
  { path: "/parents", roles: ROLES_STAFF, Component: Parents },
  { path: "/staff", roles: ROLES_ADMIN_ONLY, Component: StaffManagement },
  { path: "/classrooms/:classroomId/roster", roles: ROLES_ROSTER, Component: ClassroomRosterPage },
  { path: "/vehicles", roles: ROLES_STAFF, Component: Vehicles },
  { path: "/vehicles/:vehicleId", roles: ROLES_STAFF, Component: VehicleDetail },
  { path: "/reports", roles: ROLES_STAFF, Component: Reports },
  { path: "/time-tracking", roles: ROLES_TIME_TRACKING, Component: TimeTracking },
  { path: "/expenses", roles: ROLES_EXPENSES, Component: Expenses },
  { path: "/settings", roles: ROLES_ADMIN_ONLY, Component: Settings },
  { path: "/communication-center", roles: ROLES_STAFF, Component: CommunicationCenter },
];

function AnalyticsListener() {
  usePageTracking();
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AnalyticsListener />
        <AuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<RegisterParent />} />
            <Route path="/demo" element={<DemoLanding />} />

            {/* v1.3: printable classroom roster — deliberately OUTSIDE
                DashboardLayout (no sidebar/topbar/AI widget) so it prints
                as a clean, minimal attendance sheet. Still auth+role
                gated like every other roster surface. */}
            <Route
              path="/classrooms/:classroomId/roster/print"
              element={
                <ProtectedRoute allowedRoles={ROLES_ROSTER}>
                  <ClassroomRosterPrintPage />
                </ProtectedRoute>
              }
            />

            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {OPEN_DASHBOARD_ROUTES.map(({ path, index, Component }) => (
                <Route key={path} index={index} path={index ? undefined : path} element={<Component />} />
              ))}
              {ROLE_GATED_DASHBOARD_ROUTES.map(({ path, roles, Component }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute allowedRoles={roles}>
                      <Component />
                    </ProtectedRoute>
                  }
                />
              ))}
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
